import { useState, type CSSProperties } from 'react';
import { Link } from 'react-router';
import { Check, Flame, MoreHorizontal, Plus } from 'lucide-react';
import type { TodayItem } from '@habitos/engine';
import { useI18n } from '../i18n';
import { useApp } from '../state';
import { logAmount, setTaskDone, toggleBinary } from '../domain/actions';
import type { HabitRec } from '../domain/model';
import { HabitIcon } from '../ui/HabitIcon';
import { errorMessage, progressText, quotaMode, streakText, unitLabel } from '../ui/format';
import { useToast } from '../ui/Toast';
import { HabitActions } from './HabitActions';

export function HabitCard({ habit, item, streak }: { habit: HabitRec; item: TodayItem; streak: { current: number; unit: 'day' | 'week' | 'month' } | null }) {
  const { t, m } = useI18n();
  const { data, ctx } = useApp();
  const toast = useToast();
  const [menuOpen, setMenuOpen] = useState(false);
  const ue = item.ue;
  const status = item.task ?? ue?.status ?? 'pending';
  const v = ue?.version;
  const quota = v && (v.schedule.type === 'weekly_quota' || v.schedule.type === 'monthly_quota');
  const binaryToggle = habit.kind === 'build' && v?.goalType === 'binary' && !quota;
  const isQuit = habit.kind === 'quit';
  const showStreak = streak && streak.current > 0 && !habit.hideStreak && !ctx.settings.hideStreaks && habit.kind !== 'task';

  const run = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
    } catch (err) {
      toast.show(errorMessage(t, err));
    }
  };

  const wasSkipped = ue?.status === 'skipped';
  const onPrimary = () =>
    run(async () => {
      if (habit.kind === 'task') {
        await setTaskDone(habit, item.task !== 'completed');
        return;
      }
      if (binaryToggle) {
        await toggleBinary(habit, ctx.today, ctx, data.entries, data.overrides);
      } else {
        const inc = quotaMode(v?.schedule) === 'times' ? (v?.goalType === 'binary' ? 1 : (v?.target ?? 1)) : habit.defaultIncrement;
        await logAmount(habit, ctx.today, inc, ctx, data.entries, data.overrides);
      }
      if (wasSkipped) toast.show(t('habit.autoUnskip'));
    });

  const pressed = habit.kind === 'task' ? item.task === 'completed' : binaryToggle ? status === 'completed' : undefined;
  const increment = quotaMode(v?.schedule) === 'times' ? (v?.goalType === 'binary' ? 1 : (v?.target ?? 1)) : habit.defaultIncrement;
  const unit = unitLabel(m, v?.unit, increment);
  const primaryLabel =
    habit.kind === 'task' || binaryToggle
      ? `${pressed ? t('habit.actions.uncomplete') : t('habit.actions.complete')}: ${habit.name}`
      : isQuit
        ? `${t('habit.actions.recordOccurrence')}: ${habit.name}`
        : `${t('habit.actions.increment', { amount: `${increment}${unit ? ` ${unit}` : ''}` })}: ${habit.name}`;

  const over = isQuit && ue && ue.value > ue.required;
  const barValue = ue ? (isQuit ? (ue.required > 0 ? Math.min(1, ue.value / ue.required) : ue.value > 0 ? 1 : 0) : ue.progress) : 0;
  const statusLabel =
    habit.kind === 'task'
      ? t(`habit.status.${item.task}`)
      : isQuit && status === 'pending'
        ? t('habit.status.onTrack')
        : t(`habit.status.${status}`);

  return (
    <article className="habit" data-color={habit.color} data-status={status} aria-label={habit.name}>
      <div className="habit__icon">
        <HabitIcon name={habit.icon} />
      </div>
      <Link className="habit__main" to={`/habits/${habit.id}`}>
        <div className="habit__name">{habit.name}</div>
        <div className="habit__meta">
          {ue && !binaryToggle ? <span className="num">{progressText(t, m, habit, ue)}</span> : <span>{statusLabel}</span>}
          {ue && !binaryToggle && status !== 'pending' && <span className={`badge badge--${status}`}>{statusLabel}</span>}
          {habit.kind === 'task' && habit.dueDate && <span>{habit.dueDate}</span>}
          {showStreak && (
            <span className="streak num">
              <Flame size={15} aria-hidden="true" />
              <span className="visually-hidden">{t('detail.currentStreak')}: </span>
              {streakText(t, streak.current, streak.unit)}
            </span>
          )}
        </div>
      </Link>
      <div className="habit__actions">
        <button type="button" className="icon-btn" onClick={() => setMenuOpen(true)} aria-label={`${t('common.more')}: ${habit.name}`}>
          <MoreHorizontal size={22} aria-hidden="true" />
        </button>
        {/* El anillo alrededor del botón es el progreso del día (antes, una barra bajo el nombre). */}
        <button
          type="button"
          className={`check ${pressed === undefined ? 'check--plus' : ''} ${isQuit ? 'check--quit' : ''} ${over ? 'check--over' : ''}`}
          style={ue && !binaryToggle ? ({ '--p': barValue } as CSSProperties) : undefined}
          aria-pressed={pressed}
          aria-label={primaryLabel}
          onClick={onPrimary}
        >
          {pressed === undefined && status !== 'completed' ? <Plus size={22} aria-hidden="true" /> : <Check size={24} strokeWidth={3} aria-hidden="true" />}
        </button>
      </div>
      <HabitActions habit={habit} item={item} open={menuOpen} onClose={() => setMenuOpen(false)} />
    </article>
  );
}
